/** Widgets with real data today. The simple home page and the dashboard both render them. */
import { ArrowRight, Building2, Compass, HardHat, KeyRound, Settings2, UserPlus } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { useClients } from "@/api/queries/clients";
import { useNotices } from "@/api/queries/guidance";
import { useGuidance } from "@/components/guidance/GuidanceProvider";
import { Avatar, Badge, InlineError } from "@/components/ui";
import { useTenant } from "@/hooks/useTenant";
import { formatRelative } from "@/lib/format";

function Shortcut({ to, icon, title, description }: { to: string; icon: ReactNode; title: string; description: string }) {
  return (
    <li>
      <Link to={to} className="group flex items-start gap-3 rounded-md p-2.5 hover:bg-surface-hover">
        <span className="mt-0.5 text-fg-subtle group-hover:text-accent-text [&>svg]:size-4">{icon}</span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-fg">{title}</span>
          <span className="block text-sm text-fg-muted">{description}</span>
        </span>
        <ArrowRight aria-hidden className="mt-0.5 size-4 text-fg-subtle opacity-0 transition-opacity transition-fast group-hover:opacity-100" />
      </Link>
    </li>
  );
}

/** "Start here": the setup and everyday actions this person can take. */
export function ShortcutsWidget() {
  const { slug, can } = useTenant();
  const guidance = useGuidance();
  const base = `/t/${slug}`;
  return (
    <ul className="-m-2 grid gap-1 @xl:grid-cols-2">
      {can("field.use") && <Shortcut to={`${base}/field/work`} icon={<HardHat />} title="My work" description="Your visits today and this week, and the work you're doing." />}
      {can("clients.add_client") && <Shortcut to={`${base}/clients?new=1`} icon={<Building2 />} title="Register a client" description="Organizations or individual customers." />}
      {can("team.manage_membership") && <Shortcut to={`${base}/team?invite=1`} icon={<UserPlus />} title="Invite a teammate" description="Send an invitation by email." />}
      {can("team.manage_groups") && <Shortcut to={`${base}/roles`} icon={<KeyRound />} title="Review roles" description="Decide who can do what." />}
      {can("tenant.manage_settings") && <Shortcut to={`${base}/settings`} icon={<Settings2 />} title="Company settings" description="Details, logo and accent color." />}
      <li>
        <button type="button" onClick={() => guidance.startTour("app-shell")} className="group flex w-full items-start gap-3 rounded-md p-2.5 text-left hover:bg-surface-hover">
          <span className="mt-0.5 text-fg-subtle group-hover:text-accent-text">
            <Compass aria-hidden className="size-4" />
          </span>
          <span>
            <span className="block text-sm font-medium text-fg">Take the K-SHOP tour</span>
            <span className="block text-sm text-fg-muted">A one-minute orientation.</span>
          </span>
        </button>
      </li>
    </ul>
  );
}

export function RecentClientsWidget({ limit = 6 }: { limit?: number }) {
  const { slug, can } = useTenant();
  const recent = useClients(slug, { ordering: "-updated_at", status: "active" }, can("clients.view_client"));
  const base = `/t/${slug}`;
  if (recent.isLoading) return <p className="text-sm text-fg-muted">Loading…</p>;
  if (recent.isError) return <InlineError error={recent.error} />;
  if (!recent.data?.results.length) return <p className="text-sm text-fg-muted">No clients yet.</p>;
  return (
    <ul className="-my-1.5">
      {recent.data.results.slice(0, limit).map((client) => (
        <li key={client.id}>
          <Link to={`${base}/clients/${client.id}`} className="flex items-center gap-3 rounded-md px-1 py-1.5 hover:bg-surface-hover">
            <Avatar name={client.display_name} src={client.logo_url} size="sm" square />
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-fg">{client.display_name}</span>
            {client.client_type && (
              <span className="hidden @xl:inline">
                <Badge dot={false}>{client.client_type.name}</Badge>
              </span>
            )}
            <span className="shrink-0 text-right text-xs whitespace-nowrap text-fg-subtle">{formatRelative(client.updated_at)}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function RecentClientsLink() {
  const { slug } = useTenant();
  return (
    <Link to={`/t/${slug}/clients`} className="text-sm font-medium text-accent-text hover:underline">
      All clients
    </Link>
  );
}

export function WhatsNewWidget() {
  const { slug } = useTenant();
  const notices = useNotices(slug, false);
  if (notices.isLoading) return <p className="text-sm text-fg-muted">Loading…</p>;
  if (notices.isError) return <InlineError error={notices.error} />;
  if (!notices.data?.length) return <p className="text-sm text-fg-muted">You're up to date.</p>;
  return (
    <ul className="flex flex-col gap-3">
      {notices.data.slice(0, 3).map((n) => (
        <li key={n.key}>
          <div className="text-sm font-medium text-fg">{n.title}</div>
          <div className="text-xs text-fg-subtle tabular">v{n.version}</div>
        </li>
      ))}
    </ul>
  );
}

export function WhatsNewLink() {
  const guidance = useGuidance();
  return (
    <button type="button" className="text-sm font-medium text-accent-text hover:underline" onClick={guidance.openWhatsNew}>
      View all
    </button>
  );
}
